import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import SendIcon from '@material-ui/icons/Send'
import EditIcon from '@material-ui/icons/Edit'
import ArchiveIcon from '@material-ui/icons/Archive'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { axiosInstance } from '../../helpers'
import {
    completeBuilder,
    requestBuilderToken,
    saveBuilderDraft,
    useSignableDoc,
    useSignableDocBuilderPreviewToken,
    useSignableDocs,
} from '../../hooks/Outreach/useSignableDocs'
import { SIGNABLE_DOC_TYPE_LABELS } from '../../models/Outreach/types'
import { Routes } from '../../navigation/config/routes'
import { SignableDocBuilderGuideButton } from './SignableDocBuilderGuide'
import {
    DOCUSEAL_BUILDER_SUBMITTER_ROLES,
    DocuSealBuilderEmbed,
} from './DocuSealBuilderEmbed'
import { SignableDocFieldMappingPanel } from './SignableDocFieldMappingPanel'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        padding: theme.spacing(3),
        width: '100%',
        maxWidth: 'none',
        boxSizing: 'border-box',
    },
    header: {
        marginBottom: theme.spacing(2),
    },
    meta: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(3),
        padding: theme.spacing(2),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.background.paper,
    },
    label: {
        fontSize: '0.75rem',
        fontWeight: 600,
        color: theme.palette.text.secondary,
        textTransform: 'uppercase',
    },
    value: {
        fontWeight: 600,
    },
    section: {
        marginBottom: theme.spacing(3),
    },
    sectionTitle: {
        fontWeight: 600,
        marginBottom: theme.spacing(1),
    },
    actions: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
    // Preview stays a readable document pane; mapping uses remaining viewport width.
    previewMappingRow: {
        display: 'grid',
        gap: theme.spacing(3),
        marginBottom: theme.spacing(3),
        gridTemplateColumns: '1fr',
        alignItems: 'start',
        '@media (min-width: 1100px)': {
            gridTemplateColumns: 'minmax(480px, 640px) minmax(0, 1fr)',
        },
    },
    previewColumn: {
        minWidth: 0,
    },
    mappingColumn: {
        minWidth: 0,
        width: '100%',
        '@media (min-width: 1100px)': {
            maxHeight: 'calc(100vh - 220px)',
            overflow: 'auto',
            position: 'sticky',
            top: theme.spacing(2),
        },
    },
    builderDialogPaper: {
        height: '100%',
        maxHeight: '100%',
        margin: 0,
        borderRadius: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    builderDialogTitle: {
        flexShrink: 0,
        paddingBottom: theme.spacing(1),
    },
    builderDialogTitleRow: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
    },
    builderDialogContent: {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        paddingTop: 0,
        '&:first-child': {
            paddingTop: 0,
        },
    },
    builderDialogActions: {
        flexShrink: 0,
        borderTop: `1px solid ${theme.palette.divider}`,
    },
    builderEmbedWrap: {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
}))

type LocationState = {
    continueSetup?: boolean
}

export const SignableDocsDetail: React.FC = () => {
    const classes = useStyles()
    const history = useHistory()
    const location = useLocation<LocationState | undefined>()
    const { id } = useParams<{ id: string }>()
    const docId = id ? parseInt(id, 10) : undefined

    const docQuery = useSignableDoc(docId)
    const doc = docQuery.data
    const builderPreviewQuery = useSignableDocBuilderPreviewToken(
        doc?.docuseal_template_id && doc.status !== 'ARCHIVED'
            ? docId
            : undefined,
        doc?.docuseal_template_id,
    )
    const {
        patchDoc,
        testSend,
        isTestSending,
        removeFromSeason,
    } = useSignableDocs()

    const [editBuilderOpen, setEditBuilderOpen] = useState(false)
    const [removeOpen, setRemoveOpen] = useState(false)
    const [isRemoving, setIsRemoving] = useState(false)
    const [isMarkingReady, setIsMarkingReady] = useState(false)
    const [builderToken, setBuilderToken] = useState<{
        host: string
        token: string
    } | null>(null)
    const [builderLoading, setBuilderLoading] = useState(false)
    const [builderSaved, setBuilderSaved] = useState(false)
    const [savedTemplate, setSavedTemplate] = useState<{
        templateId: number
        slug?: string
    } | null>(null)
    /** Bump after builder saves so the read-only preview remounts with new fields. */
    const [previewRemountKey, setPreviewRemountKey] = useState(0)
    const autoOpenedBuilderRef = useRef(false)

    const closeBuilder = useCallback(() => {
        if (builderSaved || savedTemplate) {
            // Remount read-only preview so newly placed fields appear.
            setPreviewRemountKey((key) => key + 1)
        }
        setEditBuilderOpen(false)
    }, [builderSaved, savedTemplate])

    const isDraft = doc?.status === 'DRAFT'
    const canOpenBuilder = Boolean(doc?.docuseal_template_id)

    const roleCheckQuery = useQuery({
        queryKey: ['signable-doc-role-check', docId],
        queryFn: async () => {
            const { data } = await axiosInstance.get<{ warnings: string[] }>(
                `/outreach/signable-docs/${docId}/role-check/`,
            )
            return data.warnings
        },
        enabled: doc?.status === 'READY' && docId != null,
        staleTime: 60_000,
    })

    const docuSealConsoleUrl = useMemo(() => {
        if (builderPreviewQuery.data?.host) {
            return `https://${builderPreviewQuery.data.host.replace(
                /^https?:\/\//,
                '',
            )}`
        }
        if (builderToken?.host) {
            return `https://${builderToken.host.replace(/^https?:\/\//, '')}`
        }
        return 'https://sign.ezturn.co'
    }, [builderPreviewQuery.data?.host, builderToken?.host])

    const openBuilder = useCallback(async () => {
        if (!docId || !doc?.docuseal_template_id) return
        setBuilderLoading(true)
        setBuilderSaved(false)
        setSavedTemplate(null)
        try {
            const data = await requestBuilderToken(docId, {
                template_id: doc.docuseal_template_id,
            })
            setBuilderToken(data)
            setEditBuilderOpen(true)
        } catch {
            toast.error('Failed to open document builder')
        } finally {
            setBuilderLoading(false)
        }
    }, [docId, doc?.docuseal_template_id])

    useEffect(() => {
        if (autoOpenedBuilderRef.current) return
        if (!location.state?.continueSetup) return
        if (!isDraft || !canOpenBuilder || docQuery.isLoading) return
        autoOpenedBuilderRef.current = true
        history.replace({
            pathname: location.pathname,
            search: location.search,
            state: {},
        })
        openBuilder()
    }, [
        canOpenBuilder,
        docQuery.isLoading,
        history,
        isDraft,
        location.pathname,
        location.search,
        location.state,
        openBuilder,
    ])

    useEffect(() => {
        if (!editBuilderOpen) return undefined
        const previousBodyOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            document.body.style.overflow = previousBodyOverflow
        }
    }, [editBuilderOpen])

    const markReady = async () => {
        if (!docId) return
        const templateId =
            savedTemplate?.templateId ?? doc?.docuseal_template_id
        if (!templateId) {
            toast.error('Save your fields in the builder before finishing')
            return
        }
        if (isDraft && !builderSaved && !savedTemplate) {
            toast.error('Click Save in the builder toolbar, then mark ready')
            return
        }

        setIsMarkingReady(true)
        try {
            await completeBuilder(docId, {
                docuseal_template_id: templateId,
                docuseal_slug:
                    savedTemplate?.slug ?? doc?.docuseal_slug ?? undefined,
            })
            toast.success('Document is ready')
            setEditBuilderOpen(false)
            docQuery.refetch()
        } catch (error: unknown) {
            const message =
                (error as { response?: { data?: { error?: string } } })
                    ?.response?.data?.error ?? 'Failed to finish setup'
            toast.error(message)
        } finally {
            setIsMarkingReady(false)
        }
    }

    const archiveDoc = async () => {
        if (!docId) return
        setIsRemoving(true)
        try {
            await removeFromSeason(docId)
            toast.success('Document archived')
            setRemoveOpen(false)
            history.push(`${Routes.manage.contractManager}?tab=documents`)
        } catch {
            toast.error('Failed to archive document')
        } finally {
            setIsRemoving(false)
        }
    }

    if (docQuery.isLoading) {
        return (
            <Box
                className={classes.root}
                display="flex"
                justifyContent="center"
            >
                <CircularProgress />
            </Box>
        )
    }

    if (!doc) {
        return (
            <Box className={classes.root}>
                <Typography color="error">Document not found.</Typography>
                <Button
                    component={Link}
                    to={`${Routes.manage.contractManager}?tab=documents`}
                    startIcon={<ArrowBackIcon />}
                >
                    Back
                </Button>
            </Box>
        )
    }

    return (
        <Box className={classes.root}>
            <Box className={classes.header}>
                <Button
                    component={Link}
                    to={`${Routes.manage.contractManager}?tab=documents`}
                    startIcon={<ArrowBackIcon />}
                    size="small"
                    style={{ marginBottom: 8 }}
                >
                    Back to Documents
                </Button>
                <Typography variant="h5">{doc.name}</Typography>
                <Typography variant="body2" color="textSecondary">
                    {isDraft
                        ? 'Place fields in the document builder, map where values come from, then mark ready.'
                        : 'Bind this document from Leasing Season Config when ready.'}
                </Typography>
            </Box>

            {isDraft && (
                <Box mb={2}>
                    <Alert severity="info">
                        This document is still a draft. Open the document
                        builder to place fields, save in DocuSeal, map value
                        sources below, then mark ready.
                    </Alert>
                </Box>
            )}

            <Box className={classes.meta}>
                <Box>
                    <Typography className={classes.label}>Type</Typography>
                    <Typography className={classes.value}>
                        {SIGNABLE_DOC_TYPE_LABELS[doc.document_type] ??
                            doc.document_type}
                    </Typography>
                </Box>
                <Box>
                    <Typography className={classes.label}>Status</Typography>
                    <Typography className={classes.value}>
                        {doc.status}
                    </Typography>
                </Box>
                <Box>
                    <Typography className={classes.label}>
                        DocuSeal template
                    </Typography>
                    <Typography className={classes.value}>
                        {doc.docuseal_template_id ?? '—'}
                    </Typography>
                </Box>
                <Box>
                    <Button
                        href={docuSealConsoleUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        endIcon={<OpenInNewIcon />}
                        size="small"
                    >
                        Open in DocuSeal
                    </Button>
                </Box>
            </Box>

            {roleCheckQuery.data && roleCheckQuery.data.length > 0 && (
                <Box mb={2}>
                    {roleCheckQuery.data.map((warning) => (
                        <Alert
                            key={warning}
                            severity="warning"
                            style={{ marginBottom: 8 }}
                        >
                            {warning}
                        </Alert>
                    ))}
                </Box>
            )}

            <Box className={classes.actions}>
                {canOpenBuilder && doc.status !== 'ARCHIVED' && (
                    <Button
                        color={isDraft ? 'primary' : 'default'}
                        variant={isDraft ? 'contained' : 'outlined'}
                        startIcon={<EditIcon />}
                        onClick={openBuilder}
                        disabled={builderLoading}
                    >
                        {builderLoading ? 'Opening…' : 'Document builder'}
                    </Button>
                )}
                {doc.status === 'READY' && (
                    <Button
                        startIcon={<SendIcon />}
                        color="primary"
                        onClick={() => docId && testSend(docId)}
                        disabled={isTestSending}
                    >
                        Test send to me
                    </Button>
                )}
                {doc.status !== 'ARCHIVED' && (
                    <Button
                        startIcon={<ArchiveIcon />}
                        onClick={() => setRemoveOpen(true)}
                    >
                        Archive
                    </Button>
                )}
            </Box>

            {canOpenBuilder && doc.status !== 'ARCHIVED' && docId != null && (
                <Box className={classes.previewMappingRow}>
                    <Box className={classes.previewColumn}>
                        <Typography
                            className={classes.sectionTitle}
                            variant="subtitle1"
                        >
                            Preview
                        </Typography>
                        <DocuSealBuilderEmbed
                            key={previewRemountKey}
                            host={builderPreviewQuery.data?.host ?? ''}
                            token={builderPreviewQuery.data?.token ?? ''}
                            previewOnly
                            isLoading={
                                builderPreviewQuery.isLoading ||
                                (!builderPreviewQuery.data &&
                                    !builderPreviewQuery.isError)
                            }
                            error={
                                builderPreviewQuery.isError
                                    ? 'Failed to load preview'
                                    : null
                            }
                        />
                    </Box>
                    <Box className={classes.mappingColumn}>
                        <SignableDocFieldMappingPanel
                            docId={docId}
                            onSaveMappings={async (mappings) => {
                                await patchDoc({
                                    id: docId,
                                    payload: { field_mappings: mappings },
                                })
                                docQuery.refetch()
                            }}
                            onSaved={() => docQuery.refetch()}
                        />
                    </Box>
                </Box>
            )}

            <Dialog
                open={editBuilderOpen}
                onClose={() => !isMarkingReady && closeBuilder()}
                fullScreen
                PaperProps={{ className: classes.builderDialogPaper }}
            >
                <DialogTitle
                    className={classes.builderDialogTitle}
                    disableTypography
                >
                    <Box className={classes.builderDialogTitleRow}>
                        <Box>
                            <Typography variant="h6">
                                Document builder
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {isDraft
                                    ? 'Place fields on the PDF, click Save in the builder, then Mark ready. Use a different party for each person, and name each field something you will recognize when you map it.'
                                    : 'Click Save in the builder toolbar to persist changes. Use a different party for each person, and name each field something you will recognize when you map it.'}
                            </Typography>
                        </Box>
                        <SignableDocBuilderGuideButton />
                    </Box>
                </DialogTitle>
                <DialogContent
                    className={classes.builderDialogContent}
                    style={{ overflow: 'hidden' }}
                >
                    {builderLoading && !builderToken ? (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            flex={1}
                        >
                            <CircularProgress />
                        </Box>
                    ) : (
                        builderToken && (
                            <Box className={classes.builderEmbedWrap}>
                                <DocuSealBuilderEmbed
                                    host={builderToken.host}
                                    token={builderToken.token}
                                    autosave={false}
                                    fullHeight
                                    compact
                                    hideSendButton
                                    hideSignYourselfButton
                                    helperText={null}
                                    submitterRoles={
                                        DOCUSEAL_BUILDER_SUBMITTER_ROLES
                                    }
                                    onSave={async (payload) => {
                                        if (!docId) return
                                        await saveBuilderDraft(docId, {
                                            docuseal_template_id:
                                                payload.templateId,
                                            docuseal_slug: payload.slug,
                                        })
                                        setSavedTemplate({
                                            templateId: payload.templateId,
                                            slug: payload.slug,
                                        })
                                        setBuilderSaved(true)
                                        toast.success('Template saved')
                                        docQuery.refetch()
                                    }}
                                    isLoading={false}
                                />
                            </Box>
                        )
                    )}
                </DialogContent>
                <DialogActions className={classes.builderDialogActions}>
                    <Button onClick={closeBuilder} disabled={isMarkingReady}>
                        Close
                    </Button>
                    {isDraft && (
                        <Button
                            color="primary"
                            variant="contained"
                            onClick={markReady}
                            disabled={
                                isMarkingReady ||
                                builderLoading ||
                                (!builderSaved && !savedTemplate)
                            }
                        >
                            {isMarkingReady ? 'Finishing…' : 'Mark ready'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <Dialog
                open={removeOpen}
                onClose={() => !isRemoving && setRemoveOpen(false)}
            >
                <DialogTitle>Archive this document?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" gutterBottom>
                        This archives <strong>{doc.name}</strong>. It will no
                        longer appear in the library or pipeline pickers. Past
                        signing records are kept.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setRemoveOpen(false)}
                        disabled={isRemoving}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={archiveDoc}
                        disabled={isRemoving}
                    >
                        Archive
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
